import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ShoppingBag, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/context/CartContext";

const COLLECTION_LINKS = [
  { to: "/collections", label: "All Collections" },
  { to: "/collections/for-him", label: "For Him" },
  { to: "/collections/for-her", label: "For Her" },
  { to: "/collections/unisex", label: "Unisex" },
];

const HOUSE_LINKS = [
  { to: "/about", label: "About Us" },
  { to: "/experience", label: "Experience" },
  { to: "/word-of-mouth", label: "Word of Mouth" },
];

function NavDropdown({ id, label, links, open, setOpen }) {
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        data-testid={`nav-link-${id}`}
        onClick={() => setOpen(!open)}
        className="link-line flex items-center gap-1.5 text-[0.7rem] uppercase tracking-[0.22em] font-medium"
      >
        {label}
        <ChevronDown size={12} strokeWidth={2} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={`nav-${id}-dropdown`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-1/2 -translate-x-1/2 top-full pt-4"
          >
            <div className="bg-[#F7F3EE] border border-black/10 shadow-lg min-w-[190px] py-2">
              {links.map((h) => (
                <Link
                  key={h.to}
                  to={h.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-dropdown-${h.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="block px-5 py-2.5 text-[0.68rem] uppercase tracking-[0.2em] font-medium hover:bg-[#EFE9E1] hover:text-[#BA8D53] transition-colors"
                >
                  {h.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [houseOpen, setHouseOpen] = useState(false);
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const { count, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    const onScroll = () => {};
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-[#F7F3EE]/85 backdrop-blur-md border-b border-black/10"
    >
      <div className="max-w-[1440px] mx-auto flex items-center justify-between px-5 md:px-10 h-16 md:h-20 text-[#111111]">
        <Link to="/" data-testid="nav-logo" className="font-serif-brand text-xl md:text-2xl tracking-[0.18em] font-medium">
          RESERVE <span className="text-[#BA8D53]">30</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <NavDropdown id="collections" label="Collections" links={COLLECTION_LINKS} open={collectionsOpen} setOpen={setCollectionsOpen} />
          <NavDropdown
            id="the-reserve-30"
            label={<>The Reserve <span className="text-[#BA8D53]">30</span></>}
            links={HOUSE_LINKS}
            open={houseOpen}
            setOpen={setHouseOpen}
          />
          <NavLink
            to="/contact"
            data-testid="nav-link-contact"
            className="link-line text-[0.7rem] uppercase tracking-[0.22em] font-medium"
          >
            Contact
          </NavLink>
          <NavLink
            to="/track-order"
            data-testid="nav-link-track-order"
            className="link-line text-[0.7rem] uppercase tracking-[0.22em] font-medium"
          >
            Track Order
          </NavLink>
        </nav>

        <div className="flex items-center gap-5">
          <button
            onClick={() => setCartOpen(true)}
            data-testid="nav-cart-link"
            aria-label="Open cart"
            className="relative hover:text-[#BA8D53] transition-colors"
          >
            <ShoppingBag size={19} strokeWidth={1.5} />
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-1.5 -right-2 h-4 min-w-4 px-0.5 flex items-center justify-center bg-[#BA8D53] text-white text-[0.6rem] font-medium rounded-full">
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="nav-menu-toggle"
            aria-label="Menu"
            className="lg:hidden"
            onClick={() => setOpen(!open)}
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#F7F3EE] border-b border-black/10"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              <NavLink to="/" onClick={() => setOpen(false)} className="font-serif-brand text-2xl text-[#111111]">
                Home
              </NavLink>
              <div>
                <p className="font-serif-brand text-2xl text-[#111111]">Collections</p>
                <div className="mt-3 flex flex-col gap-3 pl-4">
                  {COLLECTION_LINKS.map((h) => (
                    <Link
                      key={h.to}
                      to={h.to}
                      onClick={() => setOpen(false)}
                      className="text-[0.72rem] uppercase tracking-[0.2em] font-medium text-black/60"
                    >
                      {h.label}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="border-t border-black/10 pt-4">
                <p className="font-serif-brand text-2xl text-[#111111]">
                  The Reserve <span className="text-[#BA8D53]">30</span>
                </p>
                <div className="mt-3 flex flex-col gap-3 pl-4">
                  {HOUSE_LINKS.map((h) => (
                    <Link
                      key={h.to}
                      to={h.to}
                      onClick={() => setOpen(false)}
                      className="text-[0.72rem] uppercase tracking-[0.2em] font-medium text-black/60"
                    >
                      {h.label}
                    </Link>
                  ))}
                </div>
              </div>
              <NavLink to="/contact" onClick={() => setOpen(false)} className="font-serif-brand text-2xl text-[#111111] border-t border-black/10 pt-4">
                Contact
              </NavLink>
              <NavLink to="/track-order" onClick={() => setOpen(false)} className="font-serif-brand text-2xl text-[#111111]">
                Track Order
              </NavLink>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
